import './MonitorFormListCard.scss';

import classNames from 'classnames';
import { COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { EditMonitorFooter } from '../../monitors/monitor-form/EditMonitorFooter';
import { EditOutlined } from '@ant-design/icons';
import { isEqual } from 'lodash';
import { MonitorFormTypes } from '@sonar-software/sonar-core';
import { SonarText } from '@ui/text';
import { When } from 'react-if';

import { Card, Space } from 'antd';
import { MonitorMenu, monitorMenuConfigs } from '../../monitors/monitor-form/MonitorMenu';
import React, { useCallback, useEffect, useState } from 'react';
import { useCurrentUser, UseFormListInstance } from '@hooks';

export interface MonitorFormListCardProps {
	fieldId: number
	form: UseFormListInstance<MonitorFormTypes>
	isNew: boolean
	monitors: Array<MonitorFormTypes>
	onCancel: () => void
	onDelete?: () => void
	onEdit?: () => void
	onSave: () => void
}

const intrinsicMenuItems = () => monitorMenuConfigs.filter((x) => x.intrinsic);

export const MonitorFormListCard: React.FC<MonitorFormListCardProps> = ({
	fieldId,
	form,
	isNew,
	monitors,
	onCancel,
	onDelete,
	onEdit,
	onSave
}) => {
	const [ currentMonitor, setCurrentMonitor ] = useState(form.getItem(fieldId));
	const { isReadOnly } = useCurrentUser();
	const isIntrinsic =
		currentMonitor?.monitorConfig?.monitorTypeId &&
		monitorMenuConfigs.find((x) => x.type === currentMonitor.monitorConfig?.monitorTypeId)?.intrinsic;
	const [ isEditing, setIsEditing ] = useState<boolean>(
		form.getItemState(fieldId,
			currentMonitor.id || isIntrinsic ?
				undefined :
				true).isInEdit
	);

	useEffect(() => {
		const tempMonitor = form.getItem(fieldId);

		if (!isEqual(currentMonitor, tempMonitor)) {
			setCurrentMonitor(tempMonitor);
		}
	}, [ form, fieldId, currentMonitor, setCurrentMonitor ]);

	const setEditing = useCallback((value: boolean) => {
		form.setEdit(fieldId, value, true);
		setIsEditing(value);
	}, [ fieldId, form ]);

	const handleOnClick = useCallback((e: React.MouseEvent) => {
		if (isEditing || isReadOnly) {
			return;
		}

		e.preventDefault();
		e.stopPropagation();

		if (!isReadOnly) {
			setEditing(true);
		}

		form.validate();

		if (onEdit) {
			onEdit();
		}

	}, [ form, isEditing, isReadOnly, onEdit, setEditing ]);

	const handleOnChange = useCallback((newMonitor: MonitorFormTypes) => {
		setCurrentMonitor(newMonitor);
		form.setFieldValue(
			[ 'monitors', fieldId, 'monitorConfig', 'monitorTypeId' ],
			newMonitor.monitorConfig?.monitorTypeId
		);
	}, [ form, setCurrentMonitor, fieldId ]);

	const handleOnSave = useCallback(() => {
		setEditing(false);
		onSave();
	}, [ onSave, setEditing ]);

	const handleOnCancel = useCallback(() => {
		setEditing(false);
		onCancel();
	}, [ onCancel, setEditing ]);

	const getName = useCallback(() => {
		if (!currentMonitor.id || !currentMonitor?.name) {
			return COPY_SPECIFIC.CREATE_NEW_MONITOR;
		}

		return isEditing ?
			COPY_SPECIFIC.EDITING_LABEL(currentMonitor.name) :
			currentMonitor.name;
	}, [ currentMonitor, isEditing ]);

	if (!monitors && !currentMonitor) {
		return null;
	}

	return (
		<Card
			className={ classNames('monitor-card', {
				'can-edit': !isEditing && !isReadOnly
			}) }
			extra={
				<When condition={ !isEditing }>
					<EditOutlined className='edit-icon' />
				</When>
			}
			onClick={ handleOnClick }
			title={
				<SonarText className='h3'>
					{ getName() }
				</SonarText>
			}
			variant='borderless'>
			<Space
				className='monitor-card-body'
				direction='vertical'>
				<Space
					align='start'
					direction='vertical'>
					<MonitorMenu
						editing={ isEditing }
						fieldId={ fieldId }
						hideApplications
						menuOptions='applicationRequired'
						monitor={ currentMonitor }
						onChange={ handleOnChange }
						overrideMonitors={ isNew ? monitors : [ currentMonitor ] } />
				</Space>
				<When condition={ isEditing }>
					<EditMonitorFooter
						disableSave={ !form.getItemState(fieldId).isValid }
						monitor={ currentMonitor }
						onCancel={ handleOnCancel }
						onDelete={
							!intrinsicMenuItems()
								.find((x) => x.type === currentMonitor.monitorConfig?.monitorTypeId) &&
								onDelete ? () => onDelete() : undefined
						}
						onSave={ handleOnSave } />
				</When>
			</Space>
		</Card>
	);
};
